import { CheckCircle2, Lock, CalendarClock, FileDown, FileText, Mail, RotateCcw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, download, errMsg } from "../lib/api";
import { fdate, fmonth, run, shiftMonth } from "../lib/util";
import { Alert, Btn } from "../components/kit";

function SendMail({ month }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const to = (await api.get("/s/school")).data.mapa_email;
    if (!to) return toast.error("Cadastre o e-mail para envio do mapa em Relatórios e configurações", { duration: 9000 });
    if (!window.confirm(`Enviar o mapa completo de ${fmonth(month)} (PDF e Word) para ${to}?`)) return;
    setBusy(true);
    await run(() => api.post(`/s/months/${month}/send-email`), `Mapa enviado para ${to}`);
    setBusy(false);
  };
  return <Btn variant="outline" disabled={busy} data-testid="send-map-email-button" className="px-5 py-2.5" onClick={send}><Mail className="w-4 h-4" />{busy ? "Enviando…" : "Enviar mapa por e-mail"}</Btn>;
}

export const maxMonth = (ms) => (ms.writable ? ms.work_month : ms.closed.length ? ms.closed[ms.closed.length - 1].month : shiftMonth(ms.work_month, -1));
export const closedOf = (ms, month) => ms?.closed.find((c) => c.month === month);

export function MonthBanner({ ms, month }) {
  if (!ms) return null;
  const c = closedOf(ms, month);
  if (ms.super && !c && month !== ms.work_month) return <Alert tone="info" title={`Superadmin: ${fmonth(month)}`} testid="month-super-banner"><CalendarClock className="w-4 h-4 inline mr-1" />Você está como superadmin: pode navegar, lançar e excluir em qualquer mês, sem restrição. O mês em andamento da escola é {fmonth(ms.work_month)}.</Alert>;
  if (c) return <Alert tone="green" title={`Mapa de ${fmonth(month)} concluído`} testid="month-closed-banner"><CheckCircle2 className="w-4 h-4 inline mr-1" />Concluído em {fdate(c.closed_at)} por {c.closed_by}. {ms.super ? "Como superadmin você pode alterar e excluir neste mês; o PDF já concluído não é gerado de novo." : "Somente leitura — baixe o PDF do mapa completo no botão abaixo."}</Alert>;
  if (month === ms.work_month && ms.writable) return <Alert tone="info" title={`Mapa em andamento: ${fmonth(month)}`} testid="month-work-banner"><CalendarClock className="w-4 h-4 inline mr-1" />Prazo de entrega: até {fdate(ms.deadline)}. Ao terminar, clique em "Concluir mapa do mês" para liberar o mês seguinte.</Alert>;
  if (!ms.writable && month >= ms.work_month) return <Alert tone="amber" title="Mês atual bloqueado" testid="month-locked-banner"><Lock className="w-4 h-4 inline mr-1" />O mapa de {fmonth(ms.work_month)} só pode ser feito a partir de {fmonth(shiftMonth(ms.work_month, 1))}, pois o mapa é sempre do mês anterior.</Alert>;
  return <Alert tone="amber" title="Mês sem lançamentos" testid="month-other-banner">O mês em andamento é {fmonth(ms.work_month)}.</Alert>;
}

export function MonthActions({ ms, month, acting, onChange }) {
  const c = closedOf(ms, month);
  const get = async (url, ext = "pdf") => {
    try {
      await download(url, `mapa_merenda_${month}.${ext}`);
    } catch (e) {
      toast.error(e.message || errMsg(e), { duration: 9000 });
    }
  };
  const close = async () => {
    if (!window.confirm(`Concluir o mapa de ${fmonth(month)}? O mês ficará somente leitura e o PDF completo será guardado para download.`)) return;
    if (await run(() => api.post(`/s/months/${month}/close`), "Mapa concluído — PDF disponível para download")) onChange();
  };
  const reopen = async () => {
    if (window.confirm(`Reabrir o mapa de ${fmonth(month)}?`) && (await run(() => api.post(`/s/months/${month}/reopen`), "Mês reaberto"))) onChange();
  };
  const last = ms?.closed.length && ms.closed[ms.closed.length - 1].month === month;
  if (c) return (
    <div className="flex flex-wrap gap-2">
      {acting && last && <Btn variant="ghost" data-testid="reopen-month-button" onClick={reopen}><RotateCcw className="w-4 h-4" />Reabrir mês</Btn>}
      <Btn data-testid="download-closed-pdf-button" className="px-5 py-2.5" onClick={() => get(`/s/months/${month}/pdf`)}><FileDown className="w-4 h-4" />Baixar PDF do mapa concluído</Btn>
      <Btn variant="outline" data-testid="download-map-word-button" className="px-5 py-2.5" onClick={() => get(`/s/export/mapa?month=${month}&fmt=docx`, "docx")}><FileText className="w-4 h-4" />Baixar Word do mapa</Btn>
      {acting && <SendMail month={month} />}
    </div>
  );
  const work = ms && month === ms.work_month && ms.writable;
  return (
    <div className="flex flex-wrap gap-2">
      <Btn variant={work ? "outline" : undefined} data-testid="generate-pdf-map-button" className="px-5 py-2.5" onClick={() => get(`/s/export/mapa?month=${month}`)}><FileDown className="w-4 h-4" />{work ? "Prévia do PDF" : "Gerar PDF do mapa de merenda"}</Btn>
      <Btn variant="outline" data-testid="generate-word-map-button" className="px-5 py-2.5" onClick={() => get(`/s/export/mapa?month=${month}&fmt=docx`, "docx")}><FileText className="w-4 h-4" />Baixar Word do mapa</Btn>
      {acting && <SendMail month={month} />}
      {work && <Btn data-testid="close-month-button" className="px-5 py-2.5" onClick={close}><CheckCircle2 className="w-4 h-4" />Concluir mapa do mês</Btn>}
    </div>
  );
}
